import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Filter, X } from 'lucide-react';
import api from '../../services/api';

// Helper to get default date range (1st of current month to today)
export const getDefaultDateRange = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return {
    date_from: formatDate(firstOfMonth),
    date_to: formatDate(today),
  };
};

const ReportFilters = ({ 
  filters, 
  onFilterChange, 
  onApply, 
  onClear,
  showSupplier = true,
  showCostCentre = true,
  showPaidStatus = true,
}) => {
  const [suppliers, setSuppliers] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch suppliers (approved ones)
        if (showSupplier) {
          const suppliersRes = await api.get('/suppliers/?approval_status=APPROVED');
          const suppliersData = suppliersRes.data.results || suppliersRes.data;
          setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
        }
        
        // Fetch cost centres
        if (showCostCentre) {
          const costCentresRes = await api.get('/cost-centres/');
          const costCentresData = costCentresRes.data.results || costCentresRes.data;
          setCostCentres(Array.isArray(costCentresData) ? costCentresData : []);
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [showSupplier, showCostCentre]);

  // Build options for react-select
  const supplierOptions = suppliers.map(s => ({
    value: s.supplier_name,  // Use name for filtering since PaymentScheduleRecord stores name
    label: `${s.supplier_name} (${s.service_category})`,
  }));

  const costCentreOptions = costCentres.map(cc => ({
    value: cc.id,
    label: `${cc.code} - ${cc.name}`,
  }));

  const paidStatusOptions = [
    { value: '', label: 'All' },
    { value: 'paid', label: 'Paid Only' },
    { value: 'unpaid', label: 'Unpaid Only' },
  ];

  // Shared select styles
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      minWidth: '220px',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': { borderColor: '#9ca3af' },
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? '#eff6ff' 
          : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
    }),
  };

  // Helper to find selected supplier option
  const getSupplierOption = () => {
    if (!filters.supplier) return null;
    return supplierOptions.find(opt => opt.value === filters.supplier) || null;
  };

  // Helper to find selected cost centre option
  const getCostCentreOption = () => {
    if (!filters.cost_centre) return null;
    return costCentreOptions.find(opt => opt.value === parseInt(filters.cost_centre)) || null;
  };

  const hasActiveFilters = filters.date_from || filters.date_to || 
    filters.supplier || filters.cost_centre || filters.paid_status;

  return (
    <div className="report-filters">
      <div className="filters-row">
        <div className="filter-group">
          <label>From Date</label>
          <input
            type="date"
            value={filters.date_from || ''}
            onChange={(e) => onFilterChange('date_from', e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>To Date</label>
          <input
            type="date"
            value={filters.date_to || ''}
            onChange={(e) => onFilterChange('date_to', e.target.value)}
          />
        </div>
        
        {showSupplier && (
          <div className="filter-group">
            <label>Supplier</label>
            <Select
              value={getSupplierOption()}
              onChange={(selected) => onFilterChange('supplier', selected ? selected.value : '')}
              options={supplierOptions}
              placeholder="All suppliers"
              isClearable
              isSearchable
              isLoading={loading}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        )}
        
        {showCostCentre && (
          <div className="filter-group">
            <label>Cost Centre</label>
            <Select
              value={getCostCentreOption()}
              onChange={(selected) => onFilterChange('cost_centre', selected ? selected.value : '')}
              options={costCentreOptions}
              placeholder="All cost centres"
              isClearable
              isSearchable
              isLoading={loading}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        )}
        
        {showPaidStatus && (
          <div className="filter-group">
            <label>Status</label>
            <Select
              value={paidStatusOptions.find(opt => opt.value === filters.paid_status) || paidStatusOptions[0]}
              onChange={(selected) => onFilterChange('paid_status', selected ? selected.value : '')}
              options={paidStatusOptions}
              styles={{
                ...selectStyles,
                control: (base, state) => ({
                  ...selectStyles.control(base, state),
                  minWidth: '140px',
                }),
              }}
              menuPortalTarget={document.body}
            />
          </div>
        )}
      </div>
      
      <div className="filters-actions">
        <button className="btn btn-primary" onClick={onApply}>
          <Filter size={16} />
          Apply Filters
        </button>
        {hasActiveFilters && (
          <button className="btn btn-secondary" onClick={onClear}>
            <X size={16} />
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default ReportFilters;